import { cn } from "@/utils";
import {
  Button,
  DateValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RangeCalendar,
  RangeValue,
  Tab,
  Tabs,
} from "@heroui/react";
import { getLocalTimeZone } from "@internationalized/date";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import React from "react";
import { RiCalendarTodoLine } from "react-icons/ri";

type DateRangeActionsTypes = {
  value?: RangeTypes;
  handleChangeDateRange: (startDate: Date | null, endDate: Date | null) => void;
  disabledDateRangeOptions?: RangeTypes[];
  defaultSelectedOption?: "1d" | "1w" | "1m" | "1y" | "all";
  borderRadius?: number;
  future?: boolean;
};

const rangeTypeValues = ["1d", "1w", "1m", "1y", "all", "custom"] as const;
type RangeTypes = (typeof rangeTypeValues)[number];

export const DateRangeActions = ({
  value,
  handleChangeDateRange,
  disabledDateRangeOptions,
  defaultSelectedOption,
  future = false,
}: DateRangeActionsTypes) => {
  const [selectedOption, setSelectedOption] = React.useState<RangeTypes>(
    defaultSelectedOption || "1d",
  );
  const [customDateRange, setCustomDateRange] =
    React.useState<RangeValue<DateValue> | null>(null);

  const data = [
    { value: "1d", label: t`1D` },
    { value: "1w", label: t`7D` },
    { value: "1m", label: t`1M` },
    { value: "1y", label: t`1L` },
    { value: "all", label: t`VSE` },
    {
      value: "custom",
      label: (
        <Popover
          onOpenChange={(open) => {
            if (open) {
              // setSelectedOption("custom");
            }
          }}
        >
          <PopoverTrigger>
            <Button
              disableAnimation
              disableRipple
              className="w-full"
              isIconOnly
              variant="light"
              onPress={() => {
                // setSelectedOption("custom");
              }}
            >
              <RiCalendarTodoLine fontSize={"1.1rem"} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <RangeCalendar
              aria-label={t`Izberi datum`}
              value={customDateRange}
              onChange={(value) => {
                setCustomDateRange(value);
                setSelectedOption("custom");
                handleChangeDateRange(
                  value.start.toDate(getLocalTimeZone()),
                  value.end.toDate(getLocalTimeZone()),
                );
              }}
              classNames={{
                cellButton: [
                  "relative",
                  "after:content-['']",
                  "after:absolute",
                  "after:bottom-1",
                  "after:left-1/2",
                  "after:-translate-x-1/2",
                  "after:w-1",
                  "after:h-1",
                  "after:rounded-full",
                  "after:bg-primary",
                  "after:opacity-0",
                  "data-[today='true']:after:opacity-100",
                ],
              }}
            />
          </PopoverContent>
        </Popover>
      ),
    },
  ];

  // Filter out disabled options
  const filteredData =
    disabledDateRangeOptions && disabledDateRangeOptions.length > 0
      ? data.filter(
          (option) => !disabledDateRangeOptions.includes(option.value),
        )
      : data;

  const onChangeDateRange = (prevValue: RangeTypes, value: RangeTypes) => {
    setSelectedOption(value);

    let startDate;
    let endDate;

    const val = value === "custom" ? prevValue : value;
    switch (val) {
      case "1d":
        startDate = future
          ? dayjs().toDate()
          : dayjs().subtract(1, "day").toDate();
        endDate = future ? dayjs().toDate() : dayjs().endOf("day").toDate();
        break;
      case "1w":
        startDate = future
          ? dayjs().toDate()
          : dayjs().subtract(1, "week").toDate();
        endDate = future
          ? dayjs().add(1, "week").toDate()
          : dayjs().endOf("day").toDate();
        break;
      case "1m":
        startDate = future
          ? dayjs().toDate()
          : dayjs().subtract(1, "month").toDate();
        endDate = future
          ? dayjs().add(1, "month").toDate()
          : dayjs().endOf("day").toDate();
        break;
      case "1y":
        startDate = future
          ? dayjs().toDate()
          : dayjs().subtract(1, "year").toDate();
        endDate = future
          ? dayjs().add(1, "year").toDate()
          : dayjs().endOf("day").toDate();
        break;
      case "all":
        startDate = null;
        endDate = null;
        break;
      default:
        startDate = null;
        endDate = null;
        break;
    }

    if (prevValue === "custom") {
      setCustomDateRange(null);
    }

    if (value === "custom") {
      return;
    }

    handleChangeDateRange(startDate, endDate);
  };

  return (
    <Tabs
      fullWidth
      aria-label={t`Izberi obdobje`}
      selectedKey={selectedOption}
      onSelectionChange={(value) => {
        onChangeDateRange(selectedOption, value as RangeTypes);
      }}
      classNames={{
        tabContent: "w-full",
      }}
    >
      {filteredData.map((item) => (
        <Tab
          key={item.value}
          title={item.label}
          className={cn({
            "p-0": item.value === "custom",
          })}
        />
      ))}
    </Tabs>
  );
};
